import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import CheckoutModal from "../components/magazine/CheckoutModal";
import { makeAPI } from "../services/api";

import magazineStyles from "../styles/magazine.css?raw";

const MONTH_FORMATTER = new Intl.DateTimeFormat("en-US", {
  month: "long",
  year: "numeric"
});

const FALLBACK_COVER = "/assets/img/magazine/cover.jpg";

function formatCurrency(value, currencyType = "INR") {
  const amount = Number(value || 0);

  if (!Number.isFinite(amount)) {
    return currencyType === "INR" ? "\u20B90" : `${currencyType} 0`;
  }

  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currencyType || "INR",
      maximumFractionDigits: 0
    }).format(amount);
  } catch {
    return currencyType === "INR" ? `\u20B9${amount}` : `${currencyType} ${amount}`;
  }
}

function formatIssueMonth(issue) {
  if (!issue?.month || !issue?.year) return "";
  return MONTH_FORMATTER.format(new Date(issue.year, issue.month - 1, 1));
}

function getIssueTags(issue) {
  return Array.isArray(issue?.tags) && issue.tags.length
    ? issue.tags.slice(0, 4)
    : ["CGES", "Gaming", "Magazine"];
}

function getStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

function hasStoredToken() {
  return Boolean(localStorage.getItem("token"));
}

function formatPlanValidity(durationDays) {
  const days = Number(durationDays || 0);
  return days > 0 ? `${days} days` : "Lifetime access";
}

function getSavingsMetrics(planPrice, baselinePrice) {
  const safePlanPrice = Number(planPrice || 0);
  const safeBaselinePrice = Number(baselinePrice || 0);

  if (safeBaselinePrice <= 0 || safePlanPrice <= 0 || safePlanPrice >= safeBaselinePrice) {
    return {
      amount: 0,
      percent: 0
    };
  }

  const amount = safeBaselinePrice - safePlanPrice;
  const percent = Math.round((amount / safeBaselinePrice) * 100);

  return { amount, percent };
}

function getRemainingDays(endDate) {
  if (!endDate) return null;

  const end = new Date(endDate);
  const now = new Date();
  const diff = end.getTime() - now.getTime();

  if (!Number.isFinite(diff) || diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function formatDisplayDate(value) {
  if (!value) return "";

  return new Date(value).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric"
  });
}

function getRecentReadIds() {
  try {
    return JSON.parse(localStorage.getItem("magazine_recent_reads") || "[]");
  } catch {
    return [];
  }
}

function pushRecentRead(issue) {
  if (!issue?._id) return;

  try {
    const current = getRecentReadIds();
    const next = [String(issue._id), ...current.filter((id) => String(id) !== String(issue._id))].slice(0, 12);
    localStorage.setItem("magazine_recent_reads", JSON.stringify(next));
  } catch {
    // no-op
  }
}

export default function Magazine() {
  const navigate = useNavigate();
  const subscriptionSectionRef = useRef(null);
  const supportSectionRef = useRef(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [planForModal, setPlanForModal] = useState(null);
  const [magazineData, setMagazineData] = useState({
    current_issue: null,
    current_issue_available: false,
    past_issues: [],
    pricing_plans: [],
    current_period: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [accessState, setAccessState] = useState({
    has_active_subscription: false,
    owned_issue_ids: [],
    library_issue_ids: [],
    readable_issue_ids: [],
    active_subscription: null
  });
  const [isCancellingSubscription, setIsCancellingSubscription] = useState(false);
  const [libraryBusyId, setLibraryBusyId] = useState("");

  useEffect(() => {
    document.title = "Magazine - CGES.CLUB";
  }, []);

  useEffect(() => {
    let active = true;

    async function fetchMagazineData() {
      setIsLoading(true);
      setLoadError("");

      try {
        const response = await makeAPI("/api/get_magazine_data", "POST", false, {});

        if (!active) return;

        if (response?.status !== "S") {
          setLoadError(response?.message || "Unable to load magazine data.");
          return;
        }

        setMagazineData(response.data || {});
      } catch {
        if (!active) return;
        setLoadError("Unable to load magazine data.");
      } finally {
        if (active) {
          setIsLoading(false);
        }
      }
    }

    fetchMagazineData();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;

    async function fetchMagazineAccess() {
      if (!hasStoredToken()) {
        setAccessState({
          has_active_subscription: false,
          owned_issue_ids: [],
          library_issue_ids: [],
          readable_issue_ids: [],
          active_subscription: null
        });
        return;
      }

      try {
        const response = await makeAPI("/api/get_magazine_access", "POST", true, {});
        if (!active || response?.status !== "S") return;

        setAccessState({
          has_active_subscription: Boolean(response?.data?.has_active_subscription),
          owned_issue_ids: Array.isArray(response?.data?.owned_issue_ids) ? response.data.owned_issue_ids : [],
          library_issue_ids: Array.isArray(response?.data?.library_issue_ids) ? response.data.library_issue_ids : [],
          readable_issue_ids: Array.isArray(response?.data?.readable_issue_ids) ? response.data.readable_issue_ids : [],
          active_subscription: response?.data?.active_subscription || null
        });
      } catch {
        if (!active) return;
      }
    }

    fetchMagazineAccess();
    window.addEventListener("auth:updated", fetchMagazineAccess);

    return () => {
      active = false;
      window.removeEventListener("auth:updated", fetchMagazineAccess);
    };
  }, []);

  const currentIssue = magazineData.current_issue;
  const currentIssueAvailable = Boolean(magazineData.current_issue_available && currentIssue);
  const previousIssues = Array.isArray(magazineData.past_issues) ? magazineData.past_issues : [];
  const pricingPlans = Array.isArray(magazineData.pricing_plans) ? magazineData.pricing_plans : [];
  const allIssues = [
    ...(currentIssue ? [currentIssue] : []),
    ...previousIssues
  ];
  const recentReadIds = getRecentReadIds();

  const currentPeriodLabel = useMemo(() => {
    const period = magazineData.current_period;
    if (!period?.month || !period?.year) return "This month";
    return MONTH_FORMATTER.format(new Date(period.year, period.month - 1, 1));
  }, [magazineData.current_period]);

  const currentIssueTitle = currentIssue?.title || `${currentPeriodLabel} issue`;
  const currentIssueDescription =
    currentIssue?.description || "Current month issue will be available soon.";
  const currentIssueMonthLabel = formatIssueMonth(currentIssue) || currentPeriodLabel;
  const currentIssuePrice = currentIssue
    ? formatCurrency(currentIssue.price, currentIssue.currency_type)
    : "";
  const singleIssueAmount = Number(currentIssue?.price || 50);
  const singleIssueCurrency = currentIssue?.currency_type || "INR";
  const singleIssuePriceLabel = formatCurrency(singleIssueAmount, singleIssueCurrency);
  const currentIssueTags = getIssueTags(currentIssue);
  const lifetimeIssueMessage = "Lifetime access to this specific issue after purchase.";
  const issueHasDirectAccess = (issue) =>
    Array.isArray(accessState.owned_issue_ids) && accessState.owned_issue_ids.includes(String(issue?._id));
  const issueInLibrary = (issue) =>
    Array.isArray(accessState.library_issue_ids) && accessState.library_issue_ids.includes(String(issue?._id));
  const issueCanBeSavedToLibrary = (issue) =>
    Boolean(issue?.is_free || (accessState.has_active_subscription && !issueHasDirectAccess(issue)));
  const issueCanRead = (issue) => Boolean(issue?.is_free || accessState.has_active_subscription || issueHasDirectAccess(issue));
  const recentReadIssues = allIssues.filter(
    (issue, index, issues) =>
      recentReadIds.includes(String(issue?._id)) &&
      issues.findIndex((item) => String(item?._id) === String(issue?._id)) === index
  );
  const activePlan = pricingPlans.find(
    (plan) => String(plan?._id) === String(accessState.active_subscription?.plan_id || "")
  ) || null;
  const remainingPlanDays = getRemainingDays(accessState.active_subscription?.end_date);
  const nextAutopayDate = formatDisplayDate(accessState.active_subscription?.end_date);
  const isSubscriptionCancelled = accessState.active_subscription?.status === "cancelled";
  const refreshMagazineAccess = async () => {
    if (!hasStoredToken()) return;

    const response = await makeAPI("/api/get_magazine_access", "POST", true, {});
    if (response?.status === "S") {
      setAccessState({
        has_active_subscription: Boolean(response?.data?.has_active_subscription),
        owned_issue_ids: Array.isArray(response?.data?.owned_issue_ids) ? response.data.owned_issue_ids : [],
        library_issue_ids: Array.isArray(response?.data?.library_issue_ids) ? response.data.library_issue_ids : [],
        readable_issue_ids: Array.isArray(response?.data?.readable_issue_ids) ? response.data.readable_issue_ids : [],
        active_subscription: response?.data?.active_subscription || null
      });
    }
  };
  const bestValuePlanId = pricingPlans.reduce((bestId, plan) => {
    const currentDuration = Number(plan?.duration_days || 0);
    const bestPlan = pricingPlans.find((item) => (item.price_id || item.name) === bestId);
    const bestDuration = Number(bestPlan?.duration_days || 0);

    return currentDuration > bestDuration ? (plan.price_id || plan.name) : bestId;
  }, pricingPlans[0]?.price_id || pricingPlans[0]?.name || null);
  const monthlyPlan = pricingPlans.find((plan) => Number(plan?.duration_days || 0) > 0 && Number(plan?.duration_days || 0) <= 31) || null;
  const yearlyPlan = pricingPlans.find((plan) => Number(plan?.duration_days || 0) >= 300) || null;
  const monthlyBaseline = singleIssueAmount * 2;
  const yearlyBaseline = singleIssueAmount * 12;
  const monthlySavings = monthlyPlan ? getSavingsMetrics(monthlyPlan.price, monthlyBaseline) : { amount: 0, percent: 0 };
  const yearlySavings = yearlyPlan ? getSavingsMetrics(yearlyPlan.price, yearlyBaseline) : { amount: 0, percent: 0 };

  const openLoginModal = () => {
    if (window.UIkit && typeof window.UIkit.modal === "function") {
      window.UIkit.modal("#modal-login", { container: false }).show();
    }
  };

  const ensureLoggedIn = () => {
    const user = getStoredUser();
    if (user?.user_id) {
      return true;
    }

    openLoginModal();
    return false;
  };

  const openIssue = (issue) => {
    if (!issue?.pdf_url) return;
    if (!ensureLoggedIn()) return;
    if (!issueCanRead(issue)) {
      alert("Please buy this issue or subscribe to read it.");
      return;
    }

    pushRecentRead(issue);
    localStorage.setItem("selected_magazine_issue", JSON.stringify(issue));
    navigate(`/magazine/read/${issue.issue_number || "issue"}`, {
      state: { issue }
    });
  };

  const openIssueCheckout = (issue) => {
    if (!ensureLoggedIn()) return;

    setPlanForModal({
      name: issue?.is_free ? "Free Issue Access" : issue?.title || "Magazine Issue",
      amount: Number(issue?.price || 0),
      meta: issue?.is_free ? "free access" : "lifetime access for this issue",
      purchaseType: "issue",
      issueId: issue?._id,
      currencyType: issue?.currency_type || "INR"
    });
    setShowCheckout(true);
  };

  const openPricingCheckout = (plan) => {
    if (!ensureLoggedIn()) return;
    if (accessState.has_active_subscription) {
      alert("You already have an active subscription.");
      return;
    }
    if (!plan?._id) {
      alert("Subscription plan is not available right now.");
      return;
    }

    setPlanForModal({
      name: plan?.name || "Magazine Plan",
      amount: Number(plan?.price || 0),
      meta: `${plan?.duration_days || 0} days access`,
      purchaseType: "subscription",
      pricingId: plan?._id,
      currencyType: "INR"
    });
    setShowCheckout(true);
  };

  const handleCancelSubscription = async () => {
    if (!ensureLoggedIn()) return;
    if (!accessState.has_active_subscription || isSubscriptionCancelled) return;

    const shouldCancel = window.confirm(
      "Cancel auto-renew for your current subscription? You will keep access until the current billing period ends."
    );

    if (!shouldCancel) return;

    try {
      setIsCancellingSubscription(true);
      const response = await makeAPI("/api/cancel_magazine_subscription", "POST", true, {});

      if (response?.status !== "S") {
        alert(response?.message || "Unable to cancel subscription right now.");
        return;
      }

      if (response?.data) {
        setAccessState({
          has_active_subscription: Boolean(response?.data?.has_active_subscription),
          owned_issue_ids: Array.isArray(response?.data?.owned_issue_ids) ? response.data.owned_issue_ids : [],
          library_issue_ids: Array.isArray(response?.data?.library_issue_ids) ? response.data.library_issue_ids : [],
          readable_issue_ids: Array.isArray(response?.data?.readable_issue_ids) ? response.data.readable_issue_ids : [],
          active_subscription: response?.data?.active_subscription || null
        });
      } else {
        await refreshMagazineAccess();
      }

      alert(response?.message || "Subscription cancelled successfully.");
    } catch {
      alert("Unable to cancel subscription right now.");
    } finally {
      setIsCancellingSubscription(false);
    }
  };

  const handleLibraryToggle = async (issue) => {
    if (!issue?._id) return;
    if (!ensureLoggedIn()) return;
    if (!issueCanBeSavedToLibrary(issue)) {
      alert("Only free issues or issues available through your active subscription can be added to My Library.");
      return;
    }

    try {
      setLibraryBusyId(String(issue._id));
      const response = await makeAPI("/api/toggle_magazine_library", "POST", true, {
        issue_id: issue._id
      });

      if (response?.status !== "S") {
        alert(response?.message || "Unable to update your library.");
        return;
      }

      setAccessState({
        has_active_subscription: Boolean(response?.data?.has_active_subscription),
        owned_issue_ids: Array.isArray(response?.data?.owned_issue_ids) ? response.data.owned_issue_ids : [],
        library_issue_ids: Array.isArray(response?.data?.library_issue_ids) ? response.data.library_issue_ids : [],
        readable_issue_ids: Array.isArray(response?.data?.readable_issue_ids) ? response.data.readable_issue_ids : [],
        active_subscription: response?.data?.active_subscription || null
      });
    } catch {
      alert("Unable to update your library.");
    } finally {
      setLibraryBusyId("");
    }
  };

  const scrollToSection = (sectionRef) => {
    sectionRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  };

  return (
    <>
      <style>{magazineStyles}</style>
      <div className="container-fluid magazine-page">
      {showCheckout && (
        <CheckoutModal
          show={showCheckout}
          selectedPlan={planForModal || { name: "Single Issue", amount: 50, currencyType: "INR" }}
          onPurchaseSuccess={refreshMagazineAccess}
          onClose={() => setShowCheckout(false)}
        />
      )}

      {hasStoredToken() ? (
        <section className="magazine-section magazine-section--account-hub">
          <div className="magazine-panel magazine-hub-panel">
            <div className="magazine-hub-panel__intro">
              <div className="magazine-kicker">Magazine Hub</div>
              <h3>Quick Access</h3>
            </div>
            <div className="magazine-hub-panel__menu">
              <button
                type="button"
                className="btn magazine-btn magazine-btn--ghost"
                onClick={() => navigate("/magazine/library")}
              >
                <i className="fa-solid fa-book-open" aria-hidden="true" />
                My Library
              </button>
              <button
                type="button"
                className="btn magazine-btn magazine-btn--ghost"
                onClick={() => scrollToSection(subscriptionSectionRef)}
              >
                <i className="fa-solid fa-id-card" aria-hidden="true" />
                Subscription
              </button>
              <button
                type="button"
                className="btn magazine-btn magazine-btn--ghost"
                onClick={() => scrollToSection(supportSectionRef)}
              >
                <i className="fa-solid fa-headset" aria-hidden="true" />
                Support
              </button>
            </div>
          </div>
        </section>
      ) : null}

      <section className="magazine-hero-card">
        <div className="magazine-editorial-grid">
          <div className="magazine-editorial-intro">
            <div className="magazine-kicker">{currentIssueAvailable ? "This Month Issue" : "Coming Soon"}</div>
            <h1 className="magazine-display-title">{currentIssueTitle}</h1>
            <p className="magazine-hero-copy">
              {isLoading ? "Loading current month issue..." : currentIssueDescription}
            </p>

            <div className="magazine-issue-meta d-flex flex-wrap align-items-center gap-3">
              <span className="magazine-chip">{currentIssueMonthLabel}</span>
              {currentIssueAvailable ? (
                currentIssue.is_free ? (
                  <span className="magazine-chip magazine-chip--accent">Free Issue</span>
                ) : (
                  <span className="magazine-chip magazine-chip--accent">{currentIssuePrice}</span>
                )
              ) : (
                <span className="magazine-chip magazine-chip--ghost">Current month issue will be coming soon</span>
              )}
            </div>

            <div className="magazine-highlight-list">
              {currentIssueTags.map((item) => (
                <span key={item} className="magazine-highlight-pill">
                  {item}
                </span>
              ))}
            </div>

            <div className="d-flex flex-wrap gap-3 magazine-hero-actions magazine-hero-actions--single">
              <button
                type="button"
                className="btn magazine-btn magazine-btn--hero-subscription"
                onClick={() => scrollToSection(subscriptionSectionRef)}
              >
                <span className="magazine-btn__hero-glow" aria-hidden="true" />
                <span className="magazine-btn__hero-content">
                  <span className="magazine-btn__hero-icon" aria-hidden="true">
                    <i className="fa-solid fa-crown" />
                  </span>
                  <span className="magazine-btn__hero-copy">
                    <span className="magazine-btn__hero-kicker">Unlock every issue</span>
                    <span className="magazine-btn__hero-title">Subscription Plans</span>
                  </span>
                  <span className="magazine-btn__hero-icon" aria-hidden="true">
                    <i className="fa-solid fa-crown" />
                  </span>
                </span>
              </button>
            </div>

            {loadError ? <p className="magazine-hero-copy">{loadError}</p> : null}
          </div>

          <div className="magazine-editorial-stage">
            <div className="magazine-editorial-stage__glow" />
            <div className="magazine-cover magazine-cover--template magazine-cover--editorial">
              <div className="magazine-cover__image">
                <img
                  src={currentIssue?.cover_image || FALLBACK_COVER}
                  alt={`${currentIssueTitle} cover`}
                />
              </div>
              <div className="magazine-cover__overlay">
                <div className="magazine-cover__content magazine-cover__content--bottom">
                  <div className="magazine-cover__title">{currentIssueMonthLabel}</div>
                  {/* <div className="magazine-cover__subtitle">
                    {currentIssueAvailable
                      ? currentIssueDescription
                      : "No issue is live for the current month yet."}
                  </div> */}
                </div>
              </div>
            </div>
          </div>

          <div className="magazine-editorial-side">
            <div className="magazine-editorial-panel">
              <span className="magazine-editorial-panel__label">Inside This Magazine</span>
              <ul className="magazine-editorial-list">
                {currentIssueTags.map((tag) => (
                  <li key={tag}>{tag}</li>
                ))}
              </ul>
            </div>
            <div className="magazine-editorial-panel">
              <span className="magazine-editorial-panel__label">Get Issue Access</span>
              <strong>
                {currentIssueAvailable
                  ? currentIssue.is_free
                    ? "FREE"
                    : issueCanRead(currentIssue)
                      ? "READ NOW"
                      : currentIssuePrice
                  : "Soon"}
              </strong>
              <p>
                {currentIssueAvailable
                  ? currentIssue.is_free
                    ? "This issue is free. Sign in and open it instantly."
                    : issueCanRead(currentIssue)
                      ? "You already have access to this issue from a purchase or an active subscription."
                      : lifetimeIssueMessage
                  : "Current month issue will be published here soon."}
              </p>
              <button
                type="button"
                className="btn magazine-btn magazine-btn--primary w-100 magazine-editorial-panel__cta"
                onClick={() => (
                  currentIssueAvailable
                    ? currentIssue.is_free || issueCanRead(currentIssue)
                      ? openIssue(currentIssue)
                      : openIssueCheckout(currentIssue)
                    : null
                )}
                disabled={!currentIssueAvailable}
              >
                {currentIssueAvailable
                  ? currentIssue.is_free
                    ? "Read Free"
                    : issueCanRead(currentIssue)
                      ? "Read Issue"
                      : "Buy Issue"
                  : "Issue Coming Soon"}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="magazine-section">
        <div className="magazine-section__header">
          <div>
            <div className="magazine-kicker">Previous Issues</div>
            <h2>Archive Grid</h2>
          </div>
        </div>

        <div className="magazine-archive-grid">
          {!isLoading && previousIssues.length === 0 ? (
            <article className="magazine-carousel-card">
              <div className="magazine-mini-cover">
                <div className="magazine-mini-cover__overlay">
                  <div className="magazine-mini-cover__brand">CGES.CLUB</div>
                  <div className="magazine-mini-cover__month">Archive Soon</div>
                  <div className="magazine-mini-cover__subtitle">Past issues will appear here.</div>
                </div>
              </div>
            </article>
          ) : null}

          {previousIssues.map((issue) => (
              <article key={`${issue.year}-${issue.month}-${issue.issue_number || "issue"}`} className="magazine-carousel-card">
              <div className="magazine-mini-cover">
                <img
                  className="magazine-mini-cover__image"
                  src={issue.cover_image || FALLBACK_COVER}
                  alt={`${formatIssueMonth(issue)} cover`}
                />
                <div className="magazine-mini-cover__overlay">
                  {issueCanBeSavedToLibrary(issue) ? (
                    <div className="magazine-mini-cover__actions">
                      <button
                        type="button"
                        className="btn magazine-btn magazine-btn--ghost magazine-btn--icon"
                        onClick={() => handleLibraryToggle(issue)}
                        disabled={libraryBusyId === String(issue?._id)}
                        aria-label={issueInLibrary(issue) ? "Remove from library" : "Add to library"}
                        title={issueInLibrary(issue) ? "Remove from library" : "Add to library"}
                      >
                        <i className={`fa-solid ${issueInLibrary(issue) ? "fa-bookmark" : "fa-book-open-reader"}`} aria-hidden="true" />
                      </button>
                    </div>
                  ) : null}
                  <div className="magazine-mini-cover__brand">CGES.CLUB</div>
                  <div className="magazine-mini-cover__month">{formatIssueMonth(issue)}</div>
                  <div className="magazine-mini-cover__subtitle">{issue.description}</div>
                </div>
              </div>
                <div className="magazine-carousel-card__body">
                  <div>
                    <div className="magazine-carousel-card__month">{formatIssueMonth(issue)}</div>
                    <div className="magazine-carousel-card__label">
                    {issue.is_free
                      ? "Free issue"
                      : issueCanRead(issue)
                        ? "Unlocked"
                        : formatCurrency(issue.price, issue.currency_type)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn magazine-btn magazine-btn--primary"
                    onClick={() => (issueCanRead(issue) || issue.is_free ? openIssue(issue) : openIssueCheckout(issue))}
                  >
                    {issueCanRead(issue) ? "Read" : issue.is_free ? "Read Free" : "Buy"}
                  </button>
                </div>
              </article>
            ))}
          </div>
      </section>

      {recentReadIssues.length > 0 ? (
        <section className="magazine-section">
          <div className="magazine-section__header">
            <div>
              <div className="magazine-kicker">Continue Reading</div>
              <h2>Recent Reads</h2>
            </div>
          </div>

          <div className="magazine-archive-grid">
            {recentReadIssues.map((issue) => (
              <article key={`owned-${issue?._id || issue?.issue_number || formatIssueMonth(issue)}`} className="magazine-carousel-card">
                <div className="magazine-mini-cover">
                  <img
                    className="magazine-mini-cover__image"
                    src={issue.cover_image || FALLBACK_COVER}
                    alt={`${formatIssueMonth(issue)} cover`}
                  />
                  <div className="magazine-mini-cover__overlay">
                    {(issue.is_free || issueHasDirectAccess(issue)) ? (
                      <div className="magazine-mini-cover__actions">
                        <button
                          type="button"
                          className="btn magazine-btn magazine-btn--ghost magazine-btn--icon"
                          onClick={() => handleLibraryToggle(issue)}
                          disabled={libraryBusyId === String(issue?._id)}
                          aria-label={issueInLibrary(issue) ? "Remove from library" : "Add to library"}
                          title={issueInLibrary(issue) ? "Remove from library" : "Add to library"}
                        >
                          <i className={`fa-solid ${issueInLibrary(issue) ? "fa-bookmark" : "fa-book-open-reader"}`} aria-hidden="true" />
                        </button>
                      </div>
                    ) : null}
                    <div className="magazine-mini-cover__brand">CGES.CLUB</div>
                    <div className="magazine-mini-cover__month">{formatIssueMonth(issue)}</div>
                    {/* <div className="magazine-mini-cover__subtitle">{issue.description}</div> */}
                  </div>
                </div>
                <div className="magazine-carousel-card__body">
                  <div>
                    <div className="magazine-carousel-card__month">{formatIssueMonth(issue)}</div>
                    <div className="magazine-carousel-card__label">Recent Read</div>
                  </div>
                  <button
                    type="button"
                    className="btn magazine-btn magazine-btn--primary"
                    onClick={() => openIssue(issue)}
                  >
                    Read
                  </button>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      <section ref={subscriptionSectionRef} className="magazine-section">
        <div className="magazine-section__header">
          <div>
            <div className="magazine-kicker">Pricing Module</div>
            <h2>{accessState.has_active_subscription && activePlan ? "Your Subscription" : "Choose Access"}</h2>
          </div>
        </div>

        <div className="magazine-pricing-layout">
          {accessState.has_active_subscription && activePlan ? (
            <div className="magazine-pricing-layout__active">
              <article className="magazine-panel pricing-card pricing-card--current pricing-card--solo">
                <span className="pricing-card__badge pricing-card__badge--current">
                  {isSubscriptionCancelled ? "Cancelled Plan" : "Current Plan"}
                </span>
                <div className="pricing-card__name">{activePlan.name}</div>
                <div className="pricing-card__price">
                  {formatCurrency(activePlan.price, "INR")}
                  <span>{formatPlanValidity(activePlan.duration_days)}</span>
                </div>
                <p className="pricing-card__description">{activePlan.description}</p>
                <div className="pricing-card__current-meta">
                  <strong>
                    {isSubscriptionCancelled
                      ? `Access active until ${nextAutopayDate || "period end"}`
                      : nextAutopayDate
                        ? `Next autopay renews on ${nextAutopayDate}`
                        : "Autopay is active"}
                  </strong>
                  <span>
                    {isSubscriptionCancelled
                      ? remainingPlanDays === 0
                        ? "Your access ends today."
                        : `${remainingPlanDays} days of access remain before your subscription ends.`
                      : remainingPlanDays === 0
                        ? "Your billing cycle renews today."
                        : `${remainingPlanDays} days remain in the current billing cycle.`}
                  </span>
                </div>
                <ul className="pricing-card__features pricing-card__features--compact">
                  <li>Unlimited access to all current and archive issues</li>
                  <li>{isSubscriptionCancelled ? "Auto-renew is off for the next cycle" : "Auto-renew is enabled"}</li>
                  <li>{formatPlanValidity(activePlan.duration_days)} billing cycle</li>
                </ul>
                <button
                  type="button"
                  className="btn magazine-btn magazine-btn--primary w-100"
                  disabled
                >
                  {isSubscriptionCancelled ? "Subscription Cancelled" : "Subscription Active"}
                </button>
                <button
                  type="button"
                  className="btn magazine-btn magazine-btn--ghost w-100 pricing-card__cancel-btn"
                  onClick={handleCancelSubscription}
                  disabled={isCancellingSubscription || isSubscriptionCancelled}
                >
                  {isSubscriptionCancelled
                    ? "Auto-Renew Cancelled"
                    : isCancellingSubscription
                      ? "Cancelling..."
                      : "Cancel Subscription"}
                </button>
              </article>
            </div>
          ) : (
            <>
              <div className="magazine-panel magazine-pricing-compare magazine-pricing-compare--single-issue magazine-pricing-layout__comparison">
                <div className="magazine-pricing-compare__intro">
                  <div className="magazine-kicker">Magazine Pricing</div>
                  <h3>Choose your subscription tier</h3>
                  <p>
                    Pick the pass that matches your grind. Monthly is built for flexible access, while yearly is the strongest long-term value for readers who never want to miss a drop.
                  </p>
                </div>
                <div className="magazine-pricing-comparison-card">
                  <div className="magazine-pricing-comparison-card__row">
                    <div className="magazine-pricing-comparison-card__block">
                      <span>Single issue price</span>
                      <strong>{singleIssuePriceLabel}</strong>
                      <small>Lifetime access to one issue only</small>
                    </div>
                    {monthlyPlan ? (
                      <div className="magazine-pricing-comparison-card__block">
                        <span>Monthly vs issues</span>
                        <strong>{monthlySavings.percent > 0 ? `${monthlySavings.percent}% saved` : formatCurrency(monthlyPlan.price, "INR")}</strong>
                        <small>
                          {monthlySavings.amount > 0
                            ? `${formatCurrency(monthlySavings.amount, "INR")} lower than buying issues separately`
                            : "Unlimited access during the monthly cycle"}
                        </small>
                      </div>
                    ) : null}
                    {yearlyPlan ? (
                      <div className="magazine-pricing-comparison-card__block">
                        <span>Yearly vs issues</span>
                        <strong>{yearlySavings.percent > 0 ? `${yearlySavings.percent}% saved` : formatCurrency(yearlyPlan.price, "INR")}</strong>
                        <small>
                          {yearlySavings.amount > 0
                            ? `${formatCurrency(yearlySavings.amount, "INR")} lower than issue-by-issue buying`
                            : "Best long-term value for regular readers"}
                        </small>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="magazine-pricing-grid magazine-pricing-grid--public magazine-pricing-grid--duo magazine-pricing-layout__cards">
                {monthlyPlan ? (
                  <div className="pricing-card-shell pricing-card-shell--monthly">
                    <article className="magazine-panel pricing-card pricing-card--esports">
                      <div className="pricing-card__accent" aria-hidden="true" />
                      <div className="pricing-card__name">{monthlyPlan.name}</div>
                      <div className="pricing-card__price">
                        {formatCurrency(monthlyPlan.price, "INR")}
                        <span>{formatPlanValidity(monthlyPlan.duration_days)}</span>
                      </div>
                      <p className="pricing-card__description">{monthlyPlan.description}</p>
                      <ul className="pricing-card__features">
                        <li>Unlimited access to magazine issues</li>
                        <li>{monthlySavings.percent > 0 ? `Save ${monthlySavings.percent}% vs issue buying` : "Great short-term value"}</li>
                        <li>Access across current and past issues during the plan period</li>
                        <li>Fast digital delivery</li>
                      </ul>
                      <button
                        type="button"
                        className="btn magazine-btn magazine-btn--primary magazine-btn--pricing-subscribe w-100"
                        onClick={() => openPricingCheckout(monthlyPlan)}
                      >
                        <i className="fa-solid fa-crown" aria-hidden="true" />
                        <span>Subscribe Monthly</span>
                      </button>
                    </article>
                  </div>
                ) : null}

                {yearlyPlan ? (
                  <div className="pricing-card-shell pricing-card-shell--yearly">
                    <article className="magazine-panel pricing-card pricing-card--featured pricing-card--esports">
                      <div className="pricing-card__accent" aria-hidden="true" />
                      <span className="pricing-card__badge">Best Value</span>
                      <div className="pricing-card__name">{yearlyPlan.name}</div>
                      <div className="pricing-card__price">
                        {formatCurrency(yearlyPlan.price, "INR")}
                        <span>{formatPlanValidity(yearlyPlan.duration_days)}</span>
                      </div>
                      <p className="pricing-card__description">{yearlyPlan.description}</p>
                      <ul className="pricing-card__features">
                        <li>Unlimited access to magazine issues</li>
                        <li>{yearlySavings.percent > 0 ? `Save ${yearlySavings.percent}% vs issue buying` : "Best long-term value"}</li>
                        <li>Access across current and past issues during the plan period</li>
                        <li>Fast digital delivery</li>
                      </ul>
                      <button
                        type="button"
                        className="btn magazine-btn magazine-btn--primary magazine-btn--pricing-subscribe w-100"
                        onClick={() => openPricingCheckout(yearlyPlan)}
                      >
                        <i className="fa-solid fa-crown" aria-hidden="true" />
                        <span>Subscribe Yearly</span>
                      </button>
                    </article>
                  </div>
                ) : null}
              </div>

              <div className="magazine-panel magazine-pricing-explainer magazine-pricing-layout__explanation">
                <div className="magazine-kicker">How To Choose</div>
                <h3>Pick the plan that matches how you read</h3>
                <p>
                  Monthly is the fast-entry option when you want flexible access right now. Yearly is the power move if you follow every release, want archive coverage, and care most about savings across the full season.
                </p>
              </div>
            </>
          )}
        </div>
      </section>

      {hasStoredToken() ? (
        <section ref={supportSectionRef} className="magazine-section">
          <div className="magazine-panel magazine-support-panel">
            <div className="magazine-kicker">Support</div>
            <h3>Need help with access or billing?</h3>
            <p>
              If your issue access, subscription status, or library looks incorrect, contact the CGES.CLUB support team and include your account email plus the magazine plan or issue name.
            </p>
            <div className="magazine-support-panel__actions">
              <a className="btn magazine-btn magazine-btn--primary" href="mailto:support@cges.club">
                Email Support
              </a>
              <button
                type="button"
                className="btn magazine-btn magazine-btn--ghost"
                onClick={() => scrollToSection(subscriptionSectionRef)}
              >
                View Subscription
              </button>
            </div>
          </div>
        </section>
      ) : null}
      </div>
    </>
  );
}
